import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { adminService } from "../../features/admin/service";
import type { PaginatedResponse } from "../../features/admin/types";
import { formatIST } from "../../utils/dateUtils";
import ConfirmModal from "../../components/admin/ConfirmModal";
import Pagination from "../../components/admin/Pagination";
import { useAlertModal } from "../../components/admin/AlertModal";
import Spinner from "../../components/ui/Spinner";
import RefreshButton from "../../components/admin/RefreshButton";

const AdminCommunityPage: React.FC = () => {
  const [data, setData] = useState<PaginatedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { alertModal, showAlert } = useAlertModal();

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, page_size: pageSize };
      if (search) params.search = search;
      setData(await adminService.getCommunityPosts(params));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);
  useEffect(() => { setPage(1); }, [pageSize]);

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await adminService.deleteCommunityPost(deleteTarget.id);
      setDeleteTarget(null);
      fetchPosts();
    } catch (err: any) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      showAlert(typeof detail === "string" ? detail : "Failed to delete post.");
    } finally {
      setDeleting(false);
    }
  };

  const postTypeColor = (type: string) => {
    const map: Record<string, string> = {
      question: "bg-amber-500/10 text-amber-400",
      discussion: "bg-indigo-500/10 text-indigo-400",
      poll: "bg-violet-500/10 text-violet-400",
      announcement: "bg-red-500/10 text-red-400",
      media_share: "bg-cyan-500/10 text-cyan-400",
    };
    return map[type] || "bg-gray-500/10 text-gray-400";
  };

  return (
    <div>
      <AdminPageHeader
        title="Community"
        subtitle="Moderate community posts & discussions"
        meta={`${data?.total ?? 0} posts`}
        actions={<RefreshButton onRefresh={fetchPosts} />}
      />

      <div className="mb-6">
        <input
          type="text"
          placeholder="Search posts..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="px-4 py-2.5 bg-gray-900 border border-gray-800 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 w-72"
        />
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Title</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Type</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Status</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Author</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Likes</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Created</th>
                <th className="text-right py-3 px-4 text-gray-500 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="py-20 text-center text-gray-500"><Spinner /></td></tr>
              ) : data?.items.length === 0 ? (
                <tr><td colSpan={7} className="py-20 text-center text-gray-500">No posts found</td></tr>
              ) : (
                data?.items.map((post) => (
                  <tr key={post.id} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                    <td className="py-3 px-4">
                      <p className="text-white font-medium truncate max-w-xs">{post.title || "Untitled"}</p>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-md text-xs font-medium ${postTypeColor(post.post_type)}`}>
                        {post.post_type || "—"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-400">{post.status || "—"}</td>
                    <td className="py-3 px-4 text-gray-400">{post.author_name || "—"}</td>
                    <td className="py-3 px-4 text-gray-400">{post.like_count ?? 0}</td>
                    <td className="py-3 px-4 text-gray-500 text-xs">
                      {post.created_at ? formatIST(post.created_at) : "—"}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button onClick={() => setDeleteTarget(post)} className="px-2 py-1 text-xs text-red-400 hover:bg-red-500/10 rounded-md transition-colors">Delete</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {data && data.total > 0 && (
        <Pagination
          page={page - 1}
          pageSize={pageSize}
          total={data.total}
          onPageChange={(p) => setPage(p + 1)}
          onPageSizeChange={setPageSize}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete Community Post"
          description={`"${deleteTarget.content?.slice(0, 60) || "Post"}" and all its comments will be removed.`}
          items={["Delete post from database", "Delete all comments and reactions"]}
          confirmLabel="Delete Post"
          busy={deleting}
          busyLabel="Deleting…"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
      {alertModal}
    </div>
  );
};

export default AdminCommunityPage;
